import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { MaskText, SectionLabel } from "./primitives";

const PROJECTS = [
  {
    title: "Food & Beverage",
    cat: "Branding · Campaign",
    year: "2025",
    span: "md:col-span-7 md:row-span-2",
    h: "h-[380px] md:h-full",
    img: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=1600&auto=format&fit=crop",
  },
  {
    title: "Electronics",
    cat: "Retail · Digital",
    year: "2024",
    span: "md:col-span-5",
    h: "h-[300px]",
    img: "https://images.unsplash.com/photo-1550009158-9ebf69173e03?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "Real Estate",
    cat: "Web · Digital",
    year: "2025",
    span: "md:col-span-5",
    h: "h-[300px]",
    img: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1200&auto=format&fit=crop",
  },
  {
    title: "Smartphones",
    cat: "Launch · Motion",
    year: "2024",
    span: "md:col-span-6",
    h: "h-[320px]",
    img: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?q=80&w=1400&auto=format&fit=crop",
  },
  {
    title: "Fashion & Beauty",
    cat: "Social · Content",
    year: "2025",
    span: "md:col-span-6",
    h: "h-[320px]",
    img: "https://images.unsplash.com/photo-1483985988355-763728e1935b?q=80&w=1400&auto=format&fit=crop",
  },
];

function Card({ p, i }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <motion.a
      href="#work"
      ref={ref}
      data-testid={`project-card-${i}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10% 0px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 2) * 0.08 }}
      className={`group relative block overflow-hidden clip-tl ${p.span} ${p.h}`}
    >
      <motion.img
        src={p.img}
        alt={p.title}
        style={{ y: yImg }}
        className="absolute inset-0 h-[116%] w-full object-cover transition-transform duration-700 group-hover:scale-105"
      />
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ background: "linear-gradient(180deg, rgba(10,10,10,0) 30%, rgba(10,10,10,0.9) 100%)" }}
      />
      {/* spotlight hover tint */}
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-multiply"
        style={{ background: "var(--dm-teal)" }}
      />
      <div className="absolute inset-0 p-7 flex flex-col justify-end z-10">
        <span className="overline text-[11px] text-white/70 mb-2">{p.cat}</span>
        <div className="flex items-end justify-between gap-4">
          <h3 className="font-display font-bold text-white text-3xl md:text-4xl tracking-tight leading-none">
            {p.title}
          </h3>
          <span className="grid place-items-center h-11 w-11 rounded-full bg-white/10 backdrop-blur-md border border-white/20 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500 shrink-0">
            <ArrowUpRight size={18} className="text-white" />
          </span>
        </div>
      </div>
      <span className="absolute top-6 right-6 z-10 font-oswald text-xs text-white/60">{p.year}</span>
    </motion.a>
  );
}

export default function Portfolio() {
  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative bg-dm-dark grain py-24 md:py-32"
    >
      <div className="relative z-10 mx-auto max-w-[1500px] px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14 md:mb-20">
          <div>
            <SectionLabel tone="maroon" className="text-white/80">Industries we power</SectionLabel>
            <MaskText
              lines={["Work across every", "kind of brand."]}
              className="mt-5 font-display font-extrabold text-white tracking-tighter leading-[0.95] text-4xl sm:text-5xl md:text-6xl"
            />
          </div>
          <p className="max-w-sm text-white/55 leading-relaxed">
            From food and fashion to electronics and smartphones — campaigns,
            identities and digital builds delivered across the region.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:auto-rows-[300px]">
          {PROJECTS.map((p, i) => (
            <Card key={p.title} p={p} i={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
